/**
 * Generic Model Settings
 */

import axios from 'axios'
import { clone } from 'lodash'

import { SETTINGS_DIALOG_MODE } from '@/store/settings/util'
import { axiosRequest } from '@/plugins/axios-common'
import { handleError } from '@/store/handle-error'
import { singularize } from '@/utilities'

export const GenericModelSettingsState = {
  genericModelAxiosRequest: {},
  genericModelEditMode: SETTINGS_DIALOG_MODE.detail,
  genericModelList: [],
  genericModelListFilter: '',
  genericModelLoading: false,
  genericModelMetadata: null,
  genericModelPaginator: null,
  genericModelSelectedModel: null,
  genericModelShowDialog: false,
}

export const GenericModelSettingsActions = {
  getGenericModelMetadata: async ({ commit, state }, { model }) => {
    state.genericModelAxiosRequest?.meta?.cancel()
    const cancelToken = axios.CancelToken.source()

    commit('setGenericAxiosRequest', { fun: 'meta', token: cancelToken })
    return axiosRequest.
      get(`/admin/data_list/${model}/meta`, { cancelToken: cancelToken.token }).
      then(({ data }) => {
        // Group autocompleteMulti with their all_selected_field
        const fields = data?.fields
        if (fields) data.fields = groupFields(fields)

        commit('setGenericModelMetadata', data)
      }).
      catch(handleError)
  },

  getGenericModelListSettings: async ({ commit, state }, { model, params = {} }) => {
    state.genericModelAxiosRequest?.list?.cancel()
    const cancelToken = axios.CancelToken.source()

    const searchTerm = state.genericModelListFilter
    if (searchTerm && !params.search_term) params.search_term = searchTerm

    commit('setGenericAxiosRequest', { fun: 'list', token: cancelToken })
    return axiosRequest.
      get(`/admin/data_list/${model}`, { cancelToken: cancelToken.token, params }).
      then(({ data }) => {
        commit('setGenericModelList', data?.table || data)
        commit('setGenericModelPaginator', data?.paginator)

        return data?.paginator
      }).
      catch(handleError)
  },

  clearGenericModelSettings: async ({ commit }) => {
    commit('setGenericModelMetadata', {})
    commit('setGenericModelList', [])
    commit('setGenericModelSelected', null)
  },

  showGenericModelDialog: async ({ commit, dispatch }, { model, id }) => {
    return axiosRequest.
      get(`/${model}/${id}`).
      then(({ data }) => {
        const singular = singularize(model)
        const modelData = data[singular] || data

        commit('setGenericModelSelected', modelData)
        commit('setGenericModelEditMode', SETTINGS_DIALOG_MODE.detail)
        commit('setGenericModelDialog', true)

        dispatch('setupWorkflowSettings', modelData.workflows)
        dispatch('setupAutomatedTextSettings', modelData.automated_text_template)
      }).
      catch(handleError)
  },

  showGenericModelCreateDialog: async ({ commit, dispatch, getters }) => {
    const { empty } = getters.genericModelMetadata

    dispatch('setupWorkflowSettings', [])
    dispatch('setupAutomatedTextSettings', {})
    commit('setGenericModelSelected', clone(empty))
    commit('setGenericModelEditMode', SETTINGS_DIALOG_MODE.create)
    commit('setGenericModelDialog', true)
  },

  createGenericModel: async ({ getters, dispatch }, { model, obj }) => {
    // eslint-disable-next-line camelcase
    const { fields, use_request_data } = getters.genericModelMetadata
    const workflowMeta = fields?.find(f => f.type === 'workflows')
    const workflowConfigured = getters.currentWorkflowSettingsConfigured

    if (workflowMeta && workflowConfigured) {
      obj[workflowMeta.id] = getters.currentWorkflowSettingsListProcessed
      obj.workflow_permission = getters.currentWorkflowSettingsPermissions
    }

    const automatedTextMeta = fields?.find(f => f.type === 'automatedTextTemplate')
    const automatedTextConfigured = getters.automatedTextSettingsConfigured
    if (automatedTextMeta && automatedTextConfigured) obj[automatedTextMeta.id] = getters.automatedTextSettings

    if (use_request_data?.length > 0) return dispatch('createGenericModelFormData', { model, obj })
    else {
      return axiosRequest.
        post(`/${model}`, obj).
        then(_ => dispatch('getGenericModelListSettings', { model })).
        catch(handleError)
    }
  },

  createGenericModelFormData: async ({ getters, dispatch }, { model, obj }) => {
    const requestDataFields = getters.genericModelMetadata?.use_request_data
    const singular = singularize(model)

    const formData = new FormData()
    formData.append(singular, JSON.stringify(obj))

    for (const fieldData of requestDataFields) {
      if (obj[fieldData]?.length === 0) continue
      const singularField = singularize(fieldData)

      obj[fieldData].forEach((file, index) => {
        formData.append(`${singularField}-${index}`, file)
      })
    }

    return axiosRequest.
      post(`/${model}`, formData).
      then(_ => dispatch('getGenericModelListSettings', { model })).
      catch(handleError)
  },

  updateGenericModel: async ({ getters, dispatch }, { model, obj }) => {
    // eslint-disable-next-line camelcase
    const { fields, use_request_data } = getters.genericModelMetadata

    const workflowMeta = fields?.find(f => f.type === 'workflows')
    const workflowConfigured = getters.currentWorkflowSettingsConfigured

    if (workflowMeta && workflowConfigured) {
      obj[workflowMeta.id] = getters.currentWorkflowSettingsListProcessed
      obj.workflow_permission = getters.currentWorkflowSettingsPermissions
    }

    const automatedTextMeta = fields?.find(f => f.type === 'automatedTextTemplate')
    const automatedTextConfigured = getters.automatedTextSettingsConfigured
    if (automatedTextMeta && automatedTextConfigured) obj[automatedTextMeta.id] = getters.automatedTextSettings

    if (use_request_data?.length > 0) return dispatch('updateGenericModelFormData', { model, obj })
    else {
      return axiosRequest.
        put(`/${model}/${obj.id}`, obj).
        then(_ => dispatch('getGenericModelListSettings', { model })).
        catch(handleError)
    }
  },

  updateGenericModelFormData: async ({ getters, dispatch }, { model, obj }) => {
    const requestDataFields = getters.genericModelMetadata?.use_request_data
    const singular = singularize(model)

    const formData = new FormData()
    formData.append(singular, JSON.stringify(obj))

    for (const fieldData of requestDataFields) {
      if (obj[fieldData]?.length === 0) continue
      const singularField = singularize(fieldData)

      obj[fieldData].forEach((file, index) => {
        // Ignore if is json
        if (file.blob) return
        formData.append(`${singularField}-${index}`, file)
      })
    }

    return axiosRequest.
      put(`/${model}/${obj.id}`, formData).
      then(_ => dispatch('getGenericModelListSettings', { model })).
      catch(handleError)
  },

  removeGenericModel: async ({ dispatch }, { model, id }) => {
    return axiosRequest.
      delete(`/${model}/${id}`).
      then(_ => dispatch('getGenericModelListSettings', { model })).
      catch(handleError)
  },

  downloadAttachment: async (_, blobId) => {
    // eslint-disable-next-line camelcase
    return axiosRequest.
      get('/active_storage/attachments/' + blobId + '/download_blob', { responseType: 'blob' }).
      then(({ data }) => data).
      catch(handleError)
  },

  setGenericModelListFilter: ({ commit }, payload) => commit('setGenericModelListFilter', payload),
}

export const GenericModelSettingsMutations = {
  setGanericModelLoading: (state, payload) => { state.genericModelLoading = payload },
  setGenericAxiosRequest: (state, { fun, token }) => { state.genericModelAxiosRequest[fun] = token },
  setGenericModelDialog: (state, payload) => { state.genericModelShowDialog = payload },
  setGenericModelEditMode: (state, payload) => { state.genericModelEditMode = payload },
  setGenericModelList: (state, payload) => { state.genericModelList = payload },
  setGenericModelListFilter: (state, payload) => { state.genericModelListFilter = payload },
  setGenericModelMetadata: (state, payload) => { state.genericModelMetadata = payload },
  setGenericModelPaginator: (state, payload) => { state.genericModelPaginator = payload },
  setGenericModelSelected: (state, payload) => { state.genericModelSelectedModel = payload },
}

export const GenericModelSettingsGetters = {
  genericModelListFilter: state => state.genericModelListFilter,
  genericModelMetadata: state => state.genericModelMetadata,
  genericModelPaginator: state => state.genericModelPaginator,
  genericModelSelectedMode: state => state.genericModelEditMode,
  genericModelSelectedModel: state => state.genericModelSelectedModel || {},
  genericModelShowDialog: state => state.genericModelShowDialog,
  genericModelTableData: state => ({
    headers: state.genericModelMetadata?.headers || [],
    rows: state.genericModelList || [],
  }),
  hasGenericModelMetadata: state => !!state.genericModelMetadata,
}

// Partial Utility Functions

export const groupFields = fields => {
  if (fields?.length === 0) return fields
  const fieldMap = new Map(fields.map((v, i) => [i, v]))

  fields.forEach((field, i) => {
    if (field.type === 'autocompleteMulti') {
      const targetIndex = fields.findIndex(f => f.id === field.options?.all_selected_field)
      if (!targetIndex) return

      const targetField = fieldMap.get(targetIndex)
      if (!targetField) return

      targetField.with = field
      fieldMap.delete(i)
    }
  })

  const resultFields = []

  for (const value of fieldMap.values()) {
    resultFields.push(value)
    if (value.with) resultFields.push(value.with)
  }
  return resultFields
}
